import { TeachUnit } from './models/TeachUnit'
import platformTradeGateway from '@api/gateway/PlatformTrade'
import platformBasicDataGateway from '@api/gateway/PlatformBasicData'
import Response, { ResponseStatus } from '@api/Response'
import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import store from '@/store'
import Vue from 'vue'
import $http from '@packages/request'
import { Role, RoleType, Secure } from '@api/Secure'
import TeachUnitDetails from '@api/module/common/common/models/TeachUnitDetails'
import { Region } from '@api/module/common/common/models/Region'
import { VerificationCodeResult } from '@api/model/auth/VerificationCodeResult'
import { ImageVerificationCode } from '@api/model/auth/ImageVerificationCode'
import ConfigCenterModule from '@api/module/common/config-center/ConfigCenterModule'
import basciDataGateway from '@api/gateway/BasicData-default'

interface CommonState {
  unitList: Array<TeachUnit>
  inited: boolean
}

@Module({ namespaced: true, dynamic: true, name: 'CommonModule', store })
class CommonModule extends VuexModule implements CommonState {
  // 当前项目所在的省份id
  provinceId = ''
  // 授权认证域名
  authDomain = ''
  unitList: Array<TeachUnit> = new Array<TeachUnit>()
  unitDetails: TeachUnitDetails = new TeachUnitDetails()
  inited = false
  initStatus: ResponseStatus = new ResponseStatus(200, '')
  // 城市集合
  cityList: Array<Region> = new Array<Region>()
  provinceList = new Array<Region>()
  provinceMap = {} as { [key: string]: Region }
  cityMap = {} as { [key: string]: Array<Region> }
  // 城市对应的区县
  countyMap: { [key: string]: Array<Region> } = {}

  @Action
  // 初始化通用的数据状态
  async init(): Promise<ResponseStatus> {
    if (this.inited) {
      return this.initStatus
    } else {
      this.SET_PROVINCE_ID(`${ConfigCenterModule.getApplicationByName('application.provinceId')}`)
      this.SET_AUTH_DOMAIN(`${ConfigCenterModule.getIngressByName('ingress.auth')}` || '')
      let status = await this.getUnitList()
      status = await this.getCurrentCityList()
      if (status.code === 200) {
        this.setInited(true)
        this.setInitStatus(status)
      }
      return status
    }
  }

  /**
   * 获取当前项目所在身份的所有城市信息
   */
  @Action
  @Secure({
    PlatformBasicData: ['query.getSubRegionListByParentId']
  })
  async getCurrentCityList(): Promise<ResponseStatus> {
    if (!this.cityList.length) {
      const response = await platformBasicDataGateway.getSubRegionListByParentId(this.provinceId)
      if (response.data) {
        response.data
          .filter(dto => dto.available)
          .forEach(dto => {
            this.ADD_CITY(Region.fromBaseRegionDTO(dto))
          })
      }
      return response.status
    } else {
      return new ResponseStatus(200)
    }
  }

  /**
   * 获取所有省份信息
   */
  @Action
  @Secure({
    PlatformBasicData: ['query.getSubRegionListByParentId']
  })
  async getProvinces(): Promise<ResponseStatus> {
    const response = await basciDataGateway.getSubRegionListByParentId('0')
    if (response.data) {
      const provinces = response.data.filter(dto => dto.available).map(dto => Region.fromBaseRegionDTO(dto))
      this.SET_PROVINCE_LIST(provinces)
      this.SET_PROVINCE_MAP(provinces)
    }
    return response.status
  }

  /**
   * 初始化指定省份的市级列表
   */
  @Action
  @Secure({
    PlatformBasicData: ['query.getSubRegionListByParentId']
  })
  async getCityListByProvinceId(provinceId: string): Promise<ResponseStatus> {
    let cityList = this.cityMap[provinceId]
    if (!cityList) {
      const response = await basciDataGateway.getSubRegionListByParentId(provinceId)
      if (response.data) {
        cityList = response.data.filter(dto => dto.available).map(dto => Region.fromBaseRegionDTO(dto))
        this.SET_CITY_LIST({ provinceId, cityList })
      }
      return response.status
    }
    return new ResponseStatus(200)
  }

  /**
   * 获取对应城市的区县信息
   * @param cityId
   */
  @Action
  @Secure({
    PlatformBasicData: ['query.getSubRegionListByParentId']
  })
  async getCountyListByCityId(cityId: string): Promise<ResponseStatus> {
    let countyList = this.countyMap[cityId]
    if (!countyList) {
      const response = await platformBasicDataGateway.getSubRegionListByParentId(cityId)
      if (response.data) {
        countyList = response.data.filter(dto => dto.available).map(dto => Region.fromBaseRegionDTO(dto))
        this.SET_COUNTY_LIST({ cityId, countyList })
      }
      return response.status
    }
    return new ResponseStatus(200)
  }

  // 获取培训机构列表
  @Action
  @Role([RoleType.user])
  @Secure({
    PlatformTrade: ['query.findTeachUnitPage']
  })
  async getUnitList(): Promise<ResponseStatus> {
    const response = await platformBasicDataGateway.findTeachUnitPage({
      page: {
        pageNo: 1,
        pageSize: -1
      },
      queryParam: {}
    })
    const data = response.data?.currentPageData
    if (data) {
      const unitList = data.map(dto => TeachUnit.from(dto))
      this.SET_UNIT_LIST(unitList)
    }
    return response.status
  }

  /**
   * 获取年度列表
   */
  @Action
  async getUnit(id: string): Promise<ResponseStatus> {
    const response = await platformTradeGateway.getTeachUnit(id)
    if (response.data) {
      const unitDetails: TeachUnitDetails = new TeachUnitDetails()
      unitDetails.id = response.data.unitId
      unitDetails.name = response.data.name
      unitDetails.organizationCode = response.data.organizationCode
      unitDetails.provinceId = response.data.provinceId
      unitDetails.cityId = response.data.cityId
      unitDetails.countyId = response.data.countyId
      unitDetails.status = response.data.status
      unitDetails.defaultUnitDomain = response.data.defaultUnitDomain
      this.SET_UNIT(unitDetails)
    }
    return response.status
  }

  /**
   * 申请图片验证码，如果带上phoneNumber的参数则是在发送手机短信验证码前，先验证申请图片验证码再发送短信验证码流程用
   */
  @Action
  async applyImageVerificationCode(phoneNumber?: string): Promise<Response<ImageVerificationCode>> {
    let axiosResponse

    if (phoneNumber) {
      axiosResponse = await $http.post(this.authDomain + '/web/captcha/sc/apply', { phoneNumber: phoneNumber })
    } else {
      axiosResponse = await $http.get(this.authDomain + '/web/captcha/sc/apply')
    }
    const data: any = axiosResponse.data
    const code = data.code
    const response = new Response<ImageVerificationCode>()
    if (code === 200) {
      response.status = new ResponseStatus(200)
      response.data = new ImageVerificationCode()
      response.data.ticket = data.data.captchaTicket
      response.data.base64Data = data.data.b64Image
    } else {
      response.status = new ResponseStatus(code, data.message)
    }
    return response
  }

  /**
   * 验证图片验证码，返回一个验证通过后的token，用于后续申请短信验证码时使用
   * 参数ticket：申请图片验证码返回的票据，imageCode：图片验证码值
   */
  @Action
  async validateImageCode(payload: { ticket: string; imageCode: string }): Promise<Response<VerificationCodeResult>> {
    const axiosResponse = await $http.get(
      this.authDomain + '/web/captcha/sc/validate/' + payload.ticket + '/' + payload.imageCode
    )
    const data: any = axiosResponse.data
    const code = data.code
    const response = new Response<VerificationCodeResult>()
    if (code === 200) {
      response.status = new ResponseStatus(200)
      response.data = new VerificationCodeResult()
      response.data.pass = data.data.pass
      response.data.token = data.data.captchaToken
    } else {
      response.status = new ResponseStatus(code, data.message)
    }
    return response
  }

  /**
   * 在通过图片验证码验证后申请短信验证码，需要给通过图片验证码后返回的token，返回短信验证码票据
   */
  @Action
  async applySMSVerificationCodeAfterImageCode(token: string): Promise<Response<string>> {
    const axiosResponse = await $http.post(this.authDomain + '/web/captcha/sms/applywt', { captchaToken: token })
    const data: any = axiosResponse.data
    const code = data.code
    const response = new Response<string>()
    if (code === 200) {
      response.status = new ResponseStatus(200)
      response.data = data.data.captchaTicket
    } else {
      response.status = new ResponseStatus(code, data.message)
    }
    return response
  }

  /**
   * 直接获取短信验证码,用于注册，返回一个ticket用于后续验证的票据
   */
  @Action
  async applySMSVerificationCode(phoneNumber: string): Promise<Response<string>> {
    const axiosResponse = await $http.post(this.authDomain + '/web/captcha/sms/apply', { phoneNumber: phoneNumber })
    const data: any = axiosResponse.data
    const code = data.code
    const response = new Response<string>()
    if (code === 200) {
      response.status = new ResponseStatus(200)
      response.data = data.data.captchaTicket
    } else {
      response.status = new ResponseStatus(code, data.message)
    }
    return response
  }

  /**
   * 短信验证码验证，需要的参数ticket: 调用申请短信验证码后返回的票据,smsCode: 输入的短信验证码值
   * 如果验证通过则返回一个通过后签发的token
   */
  @Action
  async validateSMSCode(payload: { ticket: string; smsCode: string }): Promise<Response<VerificationCodeResult>> {
    const axiosResponse = await $http.get(
      this.authDomain + '/web/captcha/sms/validate/' + payload.ticket + '/' + payload.smsCode
    )
    const data: any = axiosResponse.data
    const code = data.code
    const response = new Response<VerificationCodeResult>()
    if (code === 200) {
      response.status = new ResponseStatus(200)
      response.data = new VerificationCodeResult()
      response.data.pass = data.data.pass
      response.data.token = data.data.captchaToken
    } else {
      response.status = new ResponseStatus(code, data.message)
    }
    return response
  }

  @Mutation
  SET_PROVINCE_ID(provinceId: string) {
    this.provinceId = provinceId
  }

  @Mutation
  SET_AUTH_DOMAIN(domain: string) {
    this.authDomain = domain
  }

  @Mutation
  ADD_CITY(city: Region) {
    this.cityList.push(city)
  }

  @Mutation
  SET_UNIT(unit: TeachUnitDetails) {
    this.unitDetails = unit
  }

  @Mutation
  private setInited(inited: boolean) {
    this.inited = inited
  }

  @Mutation
  private setInitStatus(status: ResponseStatus) {
    this.initStatus = status
  }

  @Mutation
  private SET_UNIT_LIST(unitList: Array<TeachUnit>) {
    this.unitList = unitList
  }

  @Mutation
  SET_PROVINCE_LIST(provinceList: Array<Region>) {
    this.provinceList = provinceList
  }

  @Mutation
  SET_PROVINCE_MAP(provinceList: Array<Region>) {
    provinceList.forEach(province => Vue.set(this.provinceMap, province.id, province))
  }

  @Mutation
  SET_CITY_LIST(payload: { provinceId: string; cityList: Array<Region> }) {
    Vue.set(this.cityMap, payload.provinceId, payload.cityList)
  }

  @Mutation
  SET_COUNTY_LIST(payload: { cityId: string; countyList: Array<Region> }) {
    Vue.set(this.countyMap, payload.cityId, payload.countyList)
  }

  get getUnitById() {
    return (id: string): TeachUnit | undefined => {
      return this.unitList.find(unit => unit.id === id)
    }
  }

  get countyListByCityId() {
    return (cityId: string): Array<Region> => {
      let countyList = this.countyMap[cityId]
      if (!countyList) {
        countyList = new Array<Region>()
      }
      return countyList
    }
  }
}

export default getModule(CommonModule)
